/**
 * PKI API Service
 * Handles communication with the MoodAPI backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.schmalgsicht.de';
const STREAM_BASE_URL = 'https://v2.schmalgsicht.de/pkistream';

/**
 * Check a single text field for inappropriate content
 * Called when user navigates to next page in Freundebuch
 * @param {string} field - Field name (name, city, job, customActivity, dayHighlight)
 * @param {string} value - Field value to check
 * @returns {Promise<Object>} { safe, value, replaced, ... }
 */
export async function checkField(field, value) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/check-field`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ field, value }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    // Bei Fehler: Original-Wert durchlassen
    return { safe: true, field, value, replaced: false, error: error.message };
  }
}

/**
 * Submit PKI selections to backend (Freundebuch format)
 * @param {Object} selections - Freundebuch data (name, mood, age, city, living, job, activity, interests, color, genres, dayPlan)
 * @returns {Promise<Object>} Response with profile_id
 */
export async function submitPKISelections(selections) {
  try {
    // Build request body - Freundebuch fields
    const requestBody = {
      name: selections.name,
      mood: selections.mood,
      age: selections.age,
      city: selections.city || '',
      living: selections.living,
      job: selections.job,
      activity: selections.activity,
      interests: selections.interests,
      color: selections.color,
      genres: selections.genres,
      dayPlan: selections.dayPlan || '',
      responseTimeSeconds: selections.responseTimeSeconds || 60,
    };

    const response = await fetch(`${API_BASE_URL}/api/input-values`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Poll profile status until complete
 * @param {string} profileId - Profile ID from backend
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} Complete profile data with stream_url
 */
export async function pollProfileStatus(profileId, onProgress = null) {
  const maxAttempts = 180; // Maximum 180 attempts (3 minutes with 1s interval)
  let attempts = 0;

  while (attempts < maxAttempts) {
    attempts++;

    try {
      const response = await fetch(`${API_BASE_URL}/api/profile/${profileId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const status = data.status;
      const completionPercentage = data.completion_percentage || 0;

      // Call progress callback if provided
      if (onProgress) {
        onProgress({
          status,
          completionPercentage,
          attempts,
          maxAttempts,
        });
      }

      // Check if profile is complete
      if (status === 'complete') {
        return data;
      }

      // Wait 1 second before next attempt
      await new Promise(resolve => setTimeout(resolve, 1000));

    } catch (error) {
      // Continue polling even on errors (backend might still be starting up)
      if (attempts >= maxAttempts) {
        throw error;
      }

      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  // Max attempts reached
  throw new Error('Profile generation timeout - max polling attempts reached');
}

/**
 * Trigger stream generation for a profile
 * Called after profile is complete and basic stream is playing
 * @param {string} profileId - Profile ID to generate stream for
 * @returns {Promise<Object>} Response from stream creation
 */
export async function createStream(profileId) {
  try {
    // Use backend proxy to keep API key server-side
    const response = await fetch(`${API_BASE_URL}/api/stream/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ yoshy_profile_id: profileId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Transform stream_url to use HTTPS proxy
    // Backend returns: http://schmalgsicht.de:9000/stream/abc123
    // We need: https://v2.schmalgsicht.de/pkistream/abc123
    if (data.stream_url) {
      const parts = data.stream_url.split('/stream/');
      if (parts.length > 1 && parts[1]) {
        data.stream_url = `${STREAM_BASE_URL}/${parts[1]}`;
      }
    }

    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Set activity status for a profile
 * Called when an activity (TicTacToe, Drawing) is completed
 * Sets status to true, will be reset to false when fetched by another API
 * @param {string} profileId - Profile ID
 * @param {string} activityType - Type of activity ('tictactoe' or 'drawing')
 * @returns {Promise<Object>} Response from the API
 */
export async function setActivityStatus(profileId, activityType) {
  if (!profileId) {
    return { success: false, error: 'No profileId' };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/profile/${profileId}/activity-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ activityType }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Complete PKI flow: Submit selections and poll until ready
 * @param {Object} selections - PKI selection data
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} Complete profile data
 */
export async function completePKIFlow(selections, onProgress = null) {
  try {
    // Step 1: Submit selections
    const submitResponse = await submitPKISelections(selections);
    const profileId = submitResponse.profile_id || submitResponse.id;

    if (!profileId) {
      throw new Error('No profile_id received from backend');
    }

    // Step 2: Poll until complete
    const profileData = await pollProfileStatus(profileId, onProgress);

    // Include profile_id in the return data
    return {
      ...profileData,
      profile_id: profileId
    };

  } catch (error) {
    throw error;
  }
}
